import { AlmanacFile, ViewType } from 'types'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Button as BlazeButton } from 'blaze-ui/Button'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useModals } from 'almanac-ui/Modal'
import { useProperties } from 'hooks/useProperties'
import { observer } from 'mobx-react-lite'
import { useChangeActiveSchema } from 'screens/Finder/hooks/useChangeActiveSchema'
import {
  FilesView,
  FilesViewSchema,
  FilesViewsClient,
  MinimalFilesViewSchema,
  ViewTypeToViewName,
} from 'api/clients/FilesViewsClient'
import { SelectDatePropertyModal } from '../views/CalendarView/components/SelectDatePropertyModal/SelectDatePropertyModal'
import { NewDatePropertyModal } from '../views/CalendarView/components/NewDatePropertyModal/NewDatePropertyModal'
import { useBoardView } from '../ViewSwitcher/components/CreateView/useBoardView'
import { Key } from 'react'
import { Icon as IconType } from 'almanac-ui/icons/Types'
import { Calendar2, ColumnVertical1, Grid, List, Table2 } from 'blaze-ui/icons'
import Menu6 from 'blaze-ui/icons/20/Menu6'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { newFiltersForView } from '../views/CalendarView/utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { TopBarButton } from 'blaze-components/WriterTopBar/TopBarButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const LABELS: Record<ViewType, string> = {
  [ViewType.Gallery]: 'Grid',
  [ViewType.Calendar]: 'Calendar',
  [ViewType.List]: 'List',
  [ViewType.Table]: 'Table',
  [ViewType.Board]: 'Board',
  [ViewType.Folder]: 'Folder', // Deprecated
}

const ICONS: Record<ViewType, IconType> = {
  [ViewType.Gallery]: Grid,
  [ViewType.Calendar]: Calendar2,
  [ViewType.List]: Menu6,
  [ViewType.Table]: Table2,
  [ViewType.Board]: ColumnVertical1,
  [ViewType.Folder]: Menu6, // Deprecated
}

interface SwitchViewButtonProps {
  folder: AlmanacFile
  filesView: FilesView
}

interface SwitchViewButtonContainerProps {
  folder: AlmanacFile
}

const Icon = ({ viewType }: { viewType: ViewType }) => {
  const Component = ICONS[viewType]
  return <Component />
}

const SwitchViewButton = observer(({ folder, filesView }: SwitchViewButtonProps) => {
  const viewType = filesView.schema.default_view
  const { updateAllSchemas, updateDefaultSchema, setActiveSchema, refresh: refreshFinder } = useFinderContext()
  const workspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const { dateProperties } = useProperties()
  const schemasStore = useRootStore().filesViewSchemas
  const { isMobile } = useResponsiveDesign()
  const { changeActiveSchema } = useChangeActiveSchema()

  const optimisticUpdate = (args: { viewType: ViewType; name: string; calendar_property_id: number | null }) => {
    const allSchemas = filesView.all_schemas.map((schema) => {
      if (schema.id === filesView.schema.id) {
        return {
          ...schema,
          type: args.viewType,
          name: args.name,
          calendar_property_id: args.calendar_property_id,
        }
      }

      return schema
    })

    const newSchema = {
      ...filesView.schema,
      filters: newFiltersForView(filesView, args.viewType, args.calendar_property_id),
      default_view: args.viewType,
      name: args.name,
      calendar_property_id: args.calendar_property_id,
    }

    if (filesView.schema.id === filesView.default_schema.id) {
      updateDefaultSchema(newSchema, allSchemas)
    } else {
      setActiveSchema(newSchema, allSchemas)
    }

    if (newSchema.project && newSchema.project.key) schemasStore.add(newSchema.project.key, allSchemas)
    changeActiveSchema(newSchema.id)
  }

  const rollback = (schema: FilesViewSchema, allSchemas: MinimalFilesViewSchema[], isDefault: boolean) => {
    updateAllSchemas(allSchemas)
    if (isDefault) {
      updateDefaultSchema(schema, allSchemas)
    } else {
      setActiveSchema(schema, allSchemas)
    }
    changeActiveSchema(schema.id)
  }

  const updateView = async ({
    viewType,
    calendar_property_id = null,
    calendar_property_name = null,
  }: {
    viewType: ViewType
    calendar_property_id?: number | null
    calendar_property_name?: string | null
  }) => {
    const oldSchema = filesView.schema
    const oldAllSchemas = filesView.all_schemas
    const isDefault = filesView.schema.id === filesView.default_schema.id

    let args = {
      viewType,
      name: filesView.schema.name,
      calendar_property_id,
      calendar_property_name,
    }

    if (filesView.schema.name === ViewTypeToViewName[filesView.schema.default_view]) {
      args.name = ViewTypeToViewName[args.viewType]
    }

    optimisticUpdate(args)

    const res = await FilesViewsClient.updateSchema(
      {
        ...filesView.schema,
        default_view: viewType,
        name: args.name,
        calendar_property_id,
        calendar_property_name,
        filters: newFiltersForView(filesView, viewType, calendar_property_id),
      },
      workspaceId,
    )

    refreshFinder()
    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      rollback(oldSchema, oldAllSchemas, isDefault)
    }
  }

  const { createWorkflow } = useBoardView({ filesView, callback: updateView, currentFolder: folder })

  const handleAction = async (key: Key) => {
    const viewType = key as ViewType
    if (viewType === ViewType.Calendar && dateProperties.length !== 1 && !filesView.schema.calendar_property_id) {
      if (dateProperties.length > 1) {
        openModal(SelectDatePropertyModal, {
          onConfirm: async (propertyId) => {
            updateView({ viewType, calendar_property_id: Number(propertyId) })
          },
        })
      } else {
        openModal(NewDatePropertyModal, {
          onConfirm: async (calendar_property_name: string | null) => {
            updateView({ viewType, calendar_property_name })
          },
        })
      }
      return
    }

    if (viewType === ViewType.Board) {
      createWorkflow()

      return
    }

    updateView({ viewType })
  }

  if (isMobile)
    return (
      <>
        {filesView.schema.default_view !== ViewType.Calendar ? (
          <TopBarButton
            icon={Calendar2}
            label="Mobile Calendar View"
            onPress={() => {
              handleAction(ViewType.Calendar)
            }}
          />
        ) : (
          <TopBarButton
            icon={List}
            label="Mobile List View"
            onPress={() => {
              handleAction(ViewType.List)
            }}
          />
        )}
      </>
    )

  return (
    <div className="content-calendar-tour-3" data-tour-context={LABELS[viewType]}>
      <MenuTrigger placement="bottom-end">
        <BlazeButton size="lg" variant="tertiary" withChevron="down" frontIcon={ICONS[viewType]}>
          {LABELS[viewType]}
        </BlazeButton>
        <Menu selectionMode="single" selectedKeys={[viewType]} onAction={handleAction}>
          <Item key={ViewType.Gallery}>
            <Icon viewType={ViewType.Gallery} />
            {LABELS[ViewType.Gallery]}
          </Item>
          {!folder.uploads_project && (
            <Item key={ViewType.Calendar} textValue="Calendar">
              <Icon viewType={ViewType.Calendar} />
              {LABELS[ViewType.Calendar]}
            </Item>
          )}
          {!currentWorkspace?.is_virtual_marketer && (
            <Item key={ViewType.List} textValue="Folder">
              <Icon viewType={ViewType.List} />
              {LABELS[ViewType.List]}
            </Item>
          )}
          {!folder.uploads_project && (
            <Item key={ViewType.Table} textValue="Table">
              <Icon viewType={ViewType.Table} />
              {LABELS[ViewType.Table]}
            </Item>
          )}
        </Menu>
      </MenuTrigger>
    </div>
  )
})

const SwitchViewButtonContainer = observer(({ folder }: SwitchViewButtonContainerProps) => {
  const { filesView } = useFinderContext()

  if (!filesView) return null

  return <SwitchViewButton filesView={filesView} folder={folder} />
})

export { SwitchViewButtonContainer as SwitchViewButton }
